import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2, 
  XCircle,
  Lightbulb,
  Zap,
  Flame,
  ArrowLeft,
  BookOpen,
  Flag
} from "lucide-react";
import SubjectCard from "@/components/SubjectCard";
import DoubtSolver from "@/components/DoubtSolver";
import UpgradeModal from "@/components/UpgradeModal";
import LockPanel from "@/components/LockPanel";
import TrialUnlockBanner from "@/components/TrialUnlockBanner";
import QuestionImage from "@/components/QuestionImage";
import { usePublicSettings } from "@/lib/siteSettings";

const PracticePage = () => {
  const navigate = useNavigate();
  const settings = usePublicSettings();
  const freemiumV2 = settings["features.freemium_v2"] === true;
  const pwCopy = settings["paywall_copy"] || {};
  const [trialStatus, setTrialStatus] = useState(null);

  useEffect(() => {
    if (!freemiumV2) return;
    api.get("/auth/trial/status").then(setTrialStatus).catch(() => {});
  }, [freemiumV2]);
  const { subjectId } = useParams();
  const [sp] = useSearchParams();
  const topicParam = sp.get("topic");
  const slugParam = sp.get("slug");
  const [subjects, setSubjects] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [reportedIds, setReportedIds] = useState(new Set());
  const [answerResult, setAnswerResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0, xp: 0 });
  const [progress, setProgress] = useState(null);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjectsData, progressData] = await Promise.all([
          api.get("/subjects"),
          api.get("/progress")
        ]);
        setSubjects(subjectsData.subjects);
        setProgress(progressData.progress);

        if (subjectId) {
          const isPicture = subjectId === "picture";
          let qs;
          if (isPicture) {
            qs = `/questions?has_image=true&limit=50`;
          } else if (topicParam) {
            qs = `/questions?subject_id=${subjectId}&topic_id=${encodeURIComponent(topicParam)}&limit=20`;
            if (slugParam) qs += `&tag=${encodeURIComponent(slugParam)}`;
          } else {
            qs = `/questions?subject_id=${subjectId}&limit=20&adaptive=true`;
          }
          const questionsData = await api.get(qs);
          setQuestions(questionsData.questions);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subjectId, topicParam, slugParam]);

  const currentQuestion = questions[currentIndex];
  const practiceProgress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  const handleSelectOption = (optionId) => {
    if (submitted) return;
    setSelectedOption(optionId);
  };

  const handleSubmitAnswer = async () => {
    if (!selectedOption || !currentQuestion) return;
    
    try {
      const result = await api.post("/questions/answer", {
        question_id: currentQuestion.id,
        selected_option_id: selectedOption,
        time_taken_seconds: 30 // Could add actual timer
      });
      
      setAnswerResult(result);
      setSubmitted(true);
      setSessionStats({
        correct: sessionStats.correct + (result.is_correct ? 1 : 0),
        total: sessionStats.total + 1,
        xp: sessionStats.xp + result.xp_earned + result.streak_bonus
      });

      if (result.is_correct) {
        toast.success(`+${result.xp_earned} points!`, {
          description: result.streak_bonus > 0 ? `Streak bonus: +${result.streak_bonus}` : "Great job!"
        });
      }
    } catch (error) {
      console.error("Failed to submit answer:", error);
      if (error?.response?.status === 402) {
        setLimitReached(true);
      } else {
        toast.error("Failed to submit answer");
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setSubmitted(false);
      setAnswerResult(null);
    } else {
      // End of practice session
      toast.success("Practice Complete!", {
        description: `Score: ${sessionStats.correct}/${sessionStats.total} | Points earned: ${sessionStats.xp}`
      });
      navigate("/dashboard");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading practice questions...</p>
        </div>
      </div>
    );
  }

  // Subject selection view
  if (!subjectId) {
    return (
      <div data-testid="practice-subject-selection" className="space-y-8">
        <TrialUnlockBanner />
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Practice questions</h1>
          <p className="text-muted-foreground">Pick a subject. We&apos;ll ask you questions one by one.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {subjects.map((subject) => {
            const subjectStats = progress?.subject_stats?.find(s => s.id === subject.id);
            return (
              <SubjectCard
                key={subject.id}
                subject={subject}
                stats={subjectStats}
                onClick={() => navigate(`/practice/${subject.id}`)}
                large
              />
            );
          })}
        </div>

        {/* Quick Review (Flashcards) entry */}
        <button
          data-testid="practice-flashcards-cta"
          onClick={() => navigate("/flashcards")}
          className="w-full text-left p-5 rounded-2xl glass border border-border hover:border-primary/40 transition-all flex items-center gap-4 hover-lift"
        >
          <div className="w-12 h-12 rounded-xl bg-warning/15 flex items-center justify-center flex-shrink-0">
            <Zap className="w-6 h-6 text-warning" />
          </div>
          <div className="flex-1">
            <p className="font-semibold mb-0.5">Quick Review (Flashcards)</p>
            <p className="text-sm text-muted-foreground">Short on time? Flip through key concepts in 5 minutes.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Picture Round - image-based MCQs */}
        <button
          data-testid="practice-picture-cta"
          onClick={() => navigate("/practice/picture")}
          className="w-full text-left p-5 rounded-2xl glass border border-border hover:border-primary/40 transition-all flex items-center gap-4 hover-lift mt-3"
        >
          <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold mb-0.5">Picture Round</p>
            <p className="text-sm text-muted-foreground">Practice clinical photo &amp; radiograph questions - the kind that show up in the real ADC exam.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>
    );
  }

  // No questions available
  if (questions.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground mb-4">No questions available for this subject yet.</p>
        <Button onClick={() => navigate("/practice")} variant="outline">
          Choose Another Subject
        </Button>
      </div>
    );
  }

  const currentSubject = subjects.find(s => s.id === subjectId);
  const isPictureRound = subjectId === "picture";

  return (
    <div data-testid="practice-page" className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/practice")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Subjects
        </Button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">{isPictureRound ? "Picture Round" : currentSubject?.name}</h1>
            <p className="text-sm text-muted-foreground">Question {currentIndex + 1} of {questions.length}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-bold">{sessionStats.xp}</span>
              <span className="text-muted-foreground">pts</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <span className="font-bold">{sessionStats.correct}/{sessionStats.total}</span>
            </div>
          </div>
        </div>
        <Progress value={practiceProgress} className="h-2" />
      </div>

      {/* Question Card */}
      {currentQuestion && (
        <div className="glass rounded-2xl p-6 md:p-8 border border-border mb-6">
          {/* Difficulty & Tags */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className={`text-xs px-3 py-1 rounded-full border difficulty-${currentQuestion.difficulty}`}>
              {["Easy", "Easy", "Medium", "Hard", "Expert"][currentQuestion.difficulty - 1]}
            </span>
            {currentQuestion.tags?.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary text-muted-foreground">
                {tag}
              </span>
            ))}
          </div>

          {/* Clinical Vignette */}
          {currentQuestion.clinical_vignette && (
            <div className="p-4 rounded-xl bg-secondary border border-border mb-4">
              <p className="text-sm text-muted-foreground italic">{currentQuestion.clinical_vignette}</p>
            </div>
          )}

          {/* Clinical Image (if attached) */}
          <QuestionImage src={currentQuestion.image_url} caption={currentQuestion.image_caption} />

          {/* Question Text */}
          <div className="flex items-start gap-3 mb-6">
            <h2 className="text-lg md:text-xl font-medium flex-1">{currentQuestion.question_text}</h2>
            <button
              data-testid="report-question-btn"
              onClick={async () => {
                if (reportedIds.has(currentQuestion.id)) return;
                try {
                  await api.post(`/questions/${currentQuestion.id}/report-missing-context`, { reason: "missing_context" });
                  setReportedIds((s) => new Set(s).add(currentQuestion.id));
                  toast.success("Thanks — we'll add the missing context.");
                } catch {
                  toast.error("Could not report. Try again shortly.");
                }
              }}
              disabled={reportedIds.has(currentQuestion.id)}
              title="Missing context or unclear? Flag it for us."
              className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition disabled:opacity-40 disabled:cursor-default"
            >
              <Flag className="w-3.5 h-3.5" />
              {reportedIds.has(currentQuestion.id) ? "Reported" : "Report"}
            </button>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedOption === option.id;
              const isCorrect = submitted && answerResult && option.id === answerResult.correct_option_id;
              const isIncorrect = submitted && isSelected && answerResult && option.id !== answerResult.correct_option_id;
              
              return (
                <button
                  key={option.id}
                  data-testid={`option-${option.id}`}
                  onClick={() => handleSelectOption(option.id)}
                  disabled={submitted}
                  className={`w-full text-left p-4 rounded-xl border transition-all quiz-option
                    ${isSelected && !submitted ? 'border-primary bg-primary/10' : 'border-border'}
                    ${isCorrect ? 'correct' : ''}
                    ${isIncorrect ? 'incorrect' : ''}
                    ${!submitted ? 'hover:border-primary/50' : ''}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm font-medium
                      ${isSelected && !submitted ? 'border-primary bg-primary text-white' : 'border-border'}
                      ${isCorrect ? 'border-success bg-success text-white' : ''}
                      ${isIncorrect ? 'border-destructive bg-destructive text-white' : ''}
                    `}>
                      {option.id.toUpperCase()}
                    </span>
                    <span className="flex-1">{option.text}</span>
                    {isCorrect && <CheckCircle2 className="w-5 h-5 text-success" />}
                    {isIncorrect && <XCircle className="w-5 h-5 text-destructive" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          {submitted && answerResult && (
            <div className={`mt-6 p-4 rounded-xl border ${answerResult.is_correct ? 'bg-success/10 border-success/20' : 'bg-destructive/10 border-destructive/20'}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className={`w-5 h-5 mt-0.5 ${answerResult.is_correct ? 'text-success' : 'text-destructive'}`} />
                <div className="flex-1">
                  <p className="font-medium text-sm mb-1">
                    {answerResult.is_correct ? "Correct!" : "Incorrect"}
                  </p>
                  <p className="text-sm text-muted-foreground">{answerResult.explanation}</p>
                  
                  {/* Per-option explanations */}
                  {answerResult.option_explanations && Object.keys(answerResult.option_explanations).length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Why each option:</p>
                      {currentQuestion.options.map((opt) => {
                        const optExp = answerResult.option_explanations[opt.id];
                        if (!optExp) return null;
                        const isRight = opt.id === answerResult.correct_option_id;
                        return (
                          <div key={opt.id} className={`text-xs p-2.5 rounded-lg ${isRight ? 'bg-success/10 border border-success/20' : 'bg-secondary border border-border'}`}>
                            <span className={`font-semibold ${isRight ? 'text-success' : 'text-muted-foreground'}`}>
                              {opt.id.toUpperCase()})
                            </span>{' '}
                            <span className="text-muted-foreground">{optExp}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Learn this topic link for incorrect answers */}
                  {!answerResult.is_correct && (
                    <button
                      onClick={() => navigate(`/learn/${subjectId}`)}
                      className="mt-3 flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <BookOpen className="w-4 h-4" />
                      Learn more about {currentSubject?.name}
                    </button>
                  )}

                  {/* AI Doubt Solver - the killer feature vs Winspert */}
                  <DoubtSolver
                    questionId={currentQuestion.id}
                    selectedOptionId={selectedOption}
                    testIdPrefix="practice-doubt"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between gap-3">
        <Button
          variant="outline"
          onClick={() => currentIndex > 0 && setCurrentIndex(currentIndex - 1)}
          disabled={currentIndex === 0 || !submitted}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        {!submitted ? (
          <Button
            data-testid="submit-answer-btn"
            onClick={handleSubmitAnswer}
            disabled={!selectedOption}
            className="rounded-full px-8"
          >
            Submit Answer
          </Button>
        ) : (
          <Button
            data-testid="next-question-btn"
            onClick={handleNext}
            className="rounded-full px-8"
          >
            {currentIndex < questions.length - 1 ? "Next Question" : "Complete Session"}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
      {freemiumV2 ? (
        limitReached && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="daily-cap-overlay">
            <div className="max-w-md w-full">
              <LockPanel
                wall="daily_cap"
                title={pwCopy.daily_cap_title || "You've done today's 10"}
                body={(() => {
                  let b = pwCopy.daily_cap_body || "Come back tomorrow, or go unlimited with Pro.";
                  if (trialStatus?.eligible) {
                    b += " " + (trialStatus.readiness_done
                      ? (pwCopy.daily_cap_unlock_line || "Answer {n} more questions to unlock 7 days of Pro, free").replace("{n}", trialStatus.remaining)
                      : (pwCopy.daily_cap_unlock_readiness || "Finish the readiness check to unlock 7 days of Pro, free"));
                  }
                  return b;
                })()}
                cta={pwCopy.daily_cap_cta || "Go unlimited"}
                secondaryLabel={pwCopy.daily_cap_secondary_cta || "Back to dashboard"}
                onSecondary={() => { setLimitReached(false); navigate("/dashboard"); }}
              />
            </div>
          </div>
        )
      ) : (
        <UpgradeModal
          open={limitReached}
          onClose={() => { setLimitReached(false); navigate("/dashboard"); }}
          featureName="Unlimited daily questions"
        />
      )}
    </div>
  );
};

export default PracticePage;
